const SLOT_NAMES = ["left", "center", "right"];

export const CUSTOMIZABLE_IDS = [
    "exportFile",
    "importFile",
    "undo",
    "redo",
    "shortcuts",
    "addEvent",
    "removeEvent",
    "alignLeft",
    "alignCenter",
    "alignRight",
    "writingMode",
    "position",
    "positionLineUp",
    "positionLineDown",
    "copy",
    "cut",
    "paste",
    "shiftWordBack",
    "shiftWordForward",
    "shiftLineBack",
    "shiftLineForward",
    "split",
    "merge",
    "expand",
    "compress",
    "autoFormat",
    "snapToStart",
    "snapToEnd",
];

export const EXEMPT_IDS = [
    "open",
    "save",
    "mediaImport",
    "transcription",
    "translation",
    "settings",
];

export const ICON_GROUPS = {
    exportFile: "fileIo",
    importFile: "fileIo",
    undo: "history",
    redo: "history",
    shortcuts: "history",
    addEvent: "events",
    removeEvent: "events",
    alignLeft: "align",
    alignCenter: "align",
    alignRight: "align",
    writingMode: "align",
    position: "position",
    positionLineUp: "position",
    positionLineDown: "position",
    copy: "clipboard",
    cut: "clipboard",
    paste: "clipboard",
    shiftWordBack: "shift",
    shiftWordForward: "shift",
    shiftLineBack: "shift",
    shiftLineForward: "shift",
    split: "splitMerge",
    merge: "splitMerge",
    expand: "format",
    compress: "format",
    autoFormat: "format",
    snapToStart: "snap",
    snapToEnd: "snap",
};

export const DEFAULT_IFRAME_LAYOUT = {
    left: ["undo", "redo", "shortcuts"],
    center: [],
    right: [
        "addEvent",
        "removeEvent",
        "alignLeft",
        "alignCenter",
        "alignRight",
        "writingMode",
        "position",
        "positionLineUp",
        "positionLineDown",
        "copy",
        "cut",
        "paste",
        "shiftWordBack",
        "shiftWordForward",
        "shiftLineBack",
        "shiftLineForward",
        "split",
        "merge",
        "expand",
        "compress",
        "autoFormat",
        "snapToStart",
        "snapToEnd",
    ],
};

const customizableSet = new Set(CUSTOMIZABLE_IDS);
const exemptSet = new Set(EXEMPT_IDS);

function cloneLayout(layout) {
    return {
        left: [...(layout?.left ?? [])],
        center: [...(layout?.center ?? [])],
        right: [...(layout?.right ?? [])],
    };
}

export const secondaryToolbarState = $state({
    configured: false,
    layout: cloneLayout(DEFAULT_IFRAME_LAYOUT),
});

function normalizeSlot(value, seen, ignored) {
    if (value === undefined) {
        return [];
    }
    if (!Array.isArray(value)) {
        ignored.push({
            id: String(value),
            reason: "invalid-slot",
        });
        return [];
    }

    const result = [];
    for (const rawId of value) {
        if (typeof rawId !== "string") {
            ignored.push({ id: String(rawId), reason: "unknown" });
            continue;
        }
        const id = rawId.trim();
        if (!id) {
            continue;
        }
        if (exemptSet.has(id)) {
            ignored.push({ id, reason: "exempt" });
            continue;
        }
        if (!customizableSet.has(id)) {
            ignored.push({ id, reason: "unknown" });
            continue;
        }
        if (seen.has(id)) {
            ignored.push({ id, reason: "duplicate" });
            continue;
        }
        seen.add(id);
        result.push(id);
    }
    return result;
}

export function resetSecondaryToolbar() {
    secondaryToolbarState.configured = false;
    secondaryToolbarState.layout = cloneLayout(DEFAULT_IFRAME_LAYOUT);
    return {
        success: true,
        layout: cloneLayout(secondaryToolbarState.layout),
        ignored: [],
    };
}

/**
 * Apply a full customizable-icon layout for iframe secondary toolbar.
 * `null` / `undefined` restores the default iframe layout.
 * Omitted slot keys become empty arrays.
 * @param {object | null | undefined} settings
 */
export function configureSecondaryToolbar(settings) {
    if (settings === null || settings === undefined) {
        return resetSecondaryToolbar();
    }
    if (typeof settings !== "object" || Array.isArray(settings)) {
        return {
            success: false,
            message: "Settings object is required.",
        };
    }

    const ignored = [];
    const seen = new Set();
    const layout = {
        left: normalizeSlot(settings.left, seen, ignored),
        center: normalizeSlot(settings.center, seen, ignored),
        right: normalizeSlot(settings.right, seen, ignored),
    };

    secondaryToolbarState.configured = true;
    secondaryToolbarState.layout = layout;

    return {
        success: true,
        layout: cloneLayout(layout),
        ignored,
    };
}

/**
 * Insert separator markers between adjacent icons that belong to different groups.
 * @param {string[]} slotIds
 */
export function getSlotItemsWithSeparators(slotIds) {
    const items = [];
    let prevGroup = null;
    for (const id of slotIds ?? []) {
        const group = ICON_GROUPS[id];
        if (prevGroup && group && group !== prevGroup) {
            items.push({
                type: "separator",
                key: `sep-${prevGroup}-${group}-${id}`,
            });
        }
        items.push({ type: "icon", id, key: id });
        prevGroup = group ?? id;
    }
    return items;
}

export { SLOT_NAMES };
